import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Download } from "lucide-react"
import { format } from "date-fns"
import { DashboardLayout } from "@/components/layout/DashboardLayout"

interface ImportRecord {
  id: string
  created_at: string
  file_name: string
  total_rows: number
  successful_imports: number
  failed_imports: number
  skipped_duplicates: number
  status: string
  import_details: unknown
  imported_by: string | null
  importer_name?: string
}

export default function AdminImportHistory() {
  const navigate = useNavigate()

  const { data: imports, isLoading } = useQuery({
    queryKey: ['import-history'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('import_history')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Fetch importer names for records with imported_by
      const importerIds = [...new Set(data.filter(r => r.imported_by).map(r => r.imported_by))]
      
      let importerMap: Record<string, string> = {}
      if (importerIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', importerIds)
        
        if (profiles) {
          importerMap = Object.fromEntries(profiles.map(p => [p.id, p.full_name]))
        }
      }

      return data.map(record => ({
        ...record,
        importer_name: record.imported_by ? importerMap[record.imported_by] : undefined
      })) as ImportRecord[]
    }
  })

  const downloadDetails = (importRecord: ImportRecord) => {
    const details = JSON.stringify(importRecord.import_details, null, 2)
    const blob = new Blob([details], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `import-${importRecord.id}-details.json`
    a.click()
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default'
      case 'processing':
        return 'secondary'
      case 'failed':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  return (
    <DashboardLayout>
    <div className="container mx-auto py-8 space-y-6">
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Admin
        </Button>
        <h1 className="text-3xl font-bold">Import History</h1>
        <p className="text-muted-foreground">
          View all past bulk user imports and their results
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Import Records</CardTitle>
          <CardDescription>
            Complete history of all bulk imports performed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">Loading...</p>
          ) : imports && imports.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>File Name</TableHead>
                    <TableHead>Imported By</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Success</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Skipped</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {imports.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(record.created_at), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>{record.file_name}</TableCell>
                      <TableCell>
                        {record.importer_name || 'Unknown'}
                      </TableCell>
                      <TableCell>{record.total_rows}</TableCell>
                      <TableCell className="text-primary">
                        {record.successful_imports}
                      </TableCell>
                      <TableCell className="text-destructive">
                        {record.failed_imports}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {record.skipped_duplicates}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(record.status)}>
                          {record.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {record.import_details && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => downloadDetails(record)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">
              No import history yet
            </p>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  )
}
